import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import type { AuthMethodKind } from './auth-method';
import type { CollectedDataOption } from './collected-data-option';
import type { DocumentRequestConfig } from './document-request-config';
import type { FootprintAppearance } from './footprint-appearance';
import type { SupportedIdDocTypes } from './id-doc-type';

export enum OnboardingConfigStatus {
  enabled = 'enabled',
  disabled = 'disabled',
}

export enum OnboardingConfigKind {
  kyc = 'kyc',
  kyb = 'kyb',
  auth = 'auth',
  document = 'document',
}

export type DocumentTypesAndCountries = {
  countrySpecific?: Partial<Record<Iso3166TwoDigitCountryCode, SupportedIdDocTypes[]>>;
  global?: SupportedIdDocTypes[];
};

export type KycCheck = {
  kind: 'kyc';
  data: {};
};

export type KybCheck = {
  kind: 'kyb';
  data: {
    einOnly: boolean;
  };
};

export type AmlCheck = {
  kind: 'aml';
  data: {
    ofac: boolean;
    pep: boolean;
    adverseMedia: boolean;
    continuousMonitoring: boolean;
    adverseMediaLists: Array<
      | 'financial_crime'
      | 'violent_crime'
      | 'sexual_crime'
      | 'cyber_crime'
      | 'terrorism'
      | 'fraud'
      | 'narcotics'
      | 'general_serious'
      | 'general_minor'
    >;
    matchKind: 'fuzzy_low' | 'fuzzy_medium' | 'fuzzy_high' | 'exact_name' | 'exact_name_and_dob_year';
  };
};

export type SentilinkCheck = {
  kind: 'sentilink';
  data: {};
};

export type NeuroCheck = {
  kind: 'neuro_id';
  data: {};
};

export type BusinessAmlCheck = {
  kind: 'business_aml';
  data: {};
};

export type VerificationCheck = KycCheck | KybCheck | AmlCheck | SentilinkCheck | NeuroCheck | BusinessAmlCheck;

/**
 * Feature flags for this tenant and playbook
 */
export type OnboardingFlags = {
  doNotRecordTenantOrgIdOnLogRocket?: boolean;
  fallbackToDocUploadOnCameraError?: boolean;
  idvTransferFromDesktopDisabled?: boolean;
  noBankCreditCardStatementForPoA?: boolean;
  shouldHideBootstrappedLoginWithDifferent?: boolean;
  hideFootprintFooter?: boolean;
  useBackendBootstrap?: boolean;
};

// Used in the dashboard context
export type OnboardingConfig = {
  id: string;
  playbookId: string;
  name: string;
  key: string;
  isLive: boolean;
  createdAt: string;
  status: OnboardingConfigStatus;
  appearance?: FootprintAppearance;
  mustCollectData: CollectedDataOption[];
  optionalData: CollectedDataOption[];
  isNoPhoneFlow: boolean;
  allowUsResidents: boolean;
  allowInternationalResidents: boolean;
  internationalCountryRestrictions: null | Iso3166TwoDigitCountryCode[];
  allowUsTerritoryResidents: boolean;
  supportedCountries?: Iso3166TwoDigitCountryCode[];
  isDocFirstFlow: boolean;
  skipKyc?: boolean;
  kind: OnboardingConfigKind;
  author: {
    kind: string;
    member: string;
  };
  isRulesEnabled?: boolean;
  requiredAuthMethods: AuthMethodKind[] | null;
  documentTypesAndCountries?: DocumentTypesAndCountries;
  documentsToCollect: DocumentRequestConfig[] | null;
  businessDocumentsToCollect: DocumentRequestConfig[] | null;
  promptForPasskey: boolean;
  ruleSet: {
    version: number;
  };
  verificationChecks: VerificationCheck[];
};
